import type {
  PronunciationProgress,
  PronunciationScript,
  PronunciationScriptProgress
} from '@/content/pronunciation/types'

export const PRONUNCIATION_PROGRESS_STORAGE_KEY =
  'miaozi:pronunciation-progress'
export const PRONUNCIATION_PROGRESS_CHANGED_EVENT =
  'miaozi:pronunciation-progress-changed'

export type PronunciationAnswerResult = {
  lessonId: string
  correct: boolean
  soundTags: string[]
}

export function createEmptyScriptProgress(): PronunciationScriptProgress {
  return {
    completedLessons: [],
    lessonStats: {},
    soundStats: {}
  }
}

export function createDefaultPronunciationProgress(
  now = new Date()
): PronunciationProgress {
  return {
    version: 1,
    updatedAt: now.toISOString(),
    scripts: {
      pinyin: createEmptyScriptProgress(),
      zhuyin: createEmptyScriptProgress()
    }
  }
}

export function normalizePronunciationProgress(value: unknown) {
  const fallback = createDefaultPronunciationProgress()
  if (!value || typeof value !== 'object') return fallback

  const candidate = value as Partial<PronunciationProgress>
  const scripts = candidate.scripts

  return {
    version: 1,
    updatedAt:
      typeof candidate.updatedAt === 'string' && candidate.updatedAt
        ? candidate.updatedAt
        : fallback.updatedAt,
    scripts: {
      pinyin: normalizeScriptProgress(scripts?.pinyin),
      zhuyin: normalizeScriptProgress(scripts?.zhuyin)
    }
  } satisfies PronunciationProgress
}

export function readPronunciationProgress() {
  if (typeof window === 'undefined') return createDefaultPronunciationProgress()

  try {
    const raw = window.localStorage.getItem(PRONUNCIATION_PROGRESS_STORAGE_KEY)
    return normalizePronunciationProgress(raw ? JSON.parse(raw) : null)
  } catch {
    return createDefaultPronunciationProgress()
  }
}

export function writePronunciationProgress(progress: PronunciationProgress) {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(
    PRONUNCIATION_PROGRESS_STORAGE_KEY,
    JSON.stringify(progress)
  )
  window.dispatchEvent(new Event(PRONUNCIATION_PROGRESS_CHANGED_EVENT))
}

export function recordPronunciationQuizResult({
  progress,
  script,
  lessonId,
  answers,
  completeLesson = true,
  now = new Date()
}: {
  progress: PronunciationProgress
  script: PronunciationScript
  lessonId: string
  answers: PronunciationAnswerResult[]
  completeLesson?: boolean
  now?: Date
}) {
  const next = structuredCloneProgress(progress)
  const scriptProgress = next.scripts[script]
  const correct = answers.filter((answer) => answer.correct).length
  const wrong = answers.length - correct
  const timestamp = now.toISOString()
  const currentLessonStats = scriptProgress.lessonStats[lessonId] ?? {
    attempts: 0,
    correct: 0,
    wrong: 0
  }

  scriptProgress.lessonStats[lessonId] = {
    attempts: currentLessonStats.attempts + 1,
    correct: currentLessonStats.correct + correct,
    wrong: currentLessonStats.wrong + wrong,
    lastPracticedAt: timestamp
  }

  if (completeLesson && correct / Math.max(1, answers.length) >= 0.7) {
    scriptProgress.completedLessons = Array.from(
      new Set([...scriptProgress.completedLessons, lessonId])
    )
  }

  for (const answer of answers) {
    for (const soundTag of answer.soundTags) {
      const currentSoundStats = scriptProgress.soundStats[soundTag] ?? {
        seen: 0,
        correct: 0,
        wrong: 0
      }
      scriptProgress.soundStats[soundTag] = {
        seen: currentSoundStats.seen + 1,
        correct: currentSoundStats.correct + (answer.correct ? 1 : 0),
        wrong: currentSoundStats.wrong + (answer.correct ? 0 : 1),
        lastSeenAt: timestamp
      }
    }
  }

  next.updatedAt = timestamp
  return next
}

export function getWeakSoundTags(
  progress: PronunciationProgress,
  script: PronunciationScript,
  limit = 8
) {
  return Object.entries(progress.scripts[script].soundStats)
    .filter(([, stats]) => stats.seen >= 2 && stats.wrong > 0)
    .sort(([, a], [, b]) => {
      const aRate = a.wrong / Math.max(1, a.seen)
      const bRate = b.wrong / Math.max(1, b.seen)
      if (aRate !== bRate) return bRate - aRate
      return b.wrong - a.wrong
    })
    .slice(0, limit)
    .map(([tag]) => tag)
}

export function resetPronunciationProgress(
  progress: PronunciationProgress,
  script?: PronunciationScript,
  now = new Date()
) {
  const next = structuredCloneProgress(progress)
  if (script) {
    next.scripts[script] = createEmptyScriptProgress()
  } else {
    next.scripts.pinyin = createEmptyScriptProgress()
    next.scripts.zhuyin = createEmptyScriptProgress()
  }
  next.updatedAt = now.toISOString()
  return next
}

function normalizeScriptProgress(value: unknown): PronunciationScriptProgress {
  if (!value || typeof value !== 'object') return createEmptyScriptProgress()
  const candidate = value as Partial<PronunciationScriptProgress>

  return {
    completedLessons: Array.isArray(candidate.completedLessons)
      ? candidate.completedLessons.filter(
          (lessonId): lessonId is string => typeof lessonId === 'string'
        )
      : [],
    lessonStats:
      candidate.lessonStats && typeof candidate.lessonStats === 'object'
        ? candidate.lessonStats
        : {},
    soundStats:
      candidate.soundStats && typeof candidate.soundStats === 'object'
        ? candidate.soundStats
        : {}
  }
}

function structuredCloneProgress(progress: PronunciationProgress) {
  return JSON.parse(JSON.stringify(progress)) as PronunciationProgress
}
